import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, Shield, Clock } from 'lucide-react';
import api from '../lib/api';
import { isValidEmail } from '../lib/validation';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LoginMascot } from '../components/LoginMascot';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

type FieldErrors = { email?: string; password?: string };

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [pendingNotice, setPendingNotice] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      const errorMessages: Record<string, string> = {
        session_expired: 'Your session has expired. Please log in again.',
        role_mismatch: 'Your account does not have manufacturer access.',
        invalid_session: 'Invalid session. Please log in again.',
        forbidden_role: 'Your account does not have the required permissions for the manufacturer portal.',
      };
      const message = errorMessages[error] || 'An error occurred. Please try again.';
      toast.error(message);
    }
  }, [searchParams]);

  const clearFieldError = (field: keyof FieldErrors) => {
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors: FieldErrors = {};
    if (!formData.email.trim()) {
      errors.email = 'Email is required.';
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Enter a valid email address.';
    }
    if (!formData.password) {
      errors.password = 'Password is required.';
    }
    if (errors.email || errors.password) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setPendingNotice(null);
    setLoading(true);
    try {
      const { data } = await api.post('/login', {
        email: formData.email,
        password: formData.password,
        device_name: 'Manufacturer Dashboard',
      });

      if (data.user.role !== 'manufacturer') {
        toast.error('Invalid credentials for manufacturer portal');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setLoading(false);
        return;
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      toast.success('Welcome back, ' + data.user.name + '!');
      navigate('/app/dashboard');
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || '';
      const errorLower = errorMsg.toLowerCase();

      // Account not verified/approved yet — show a persistent notice, not just a toast
      if (err.response?.status === 403) {
        if (errorLower.includes('pending') || errorLower.includes('under review') || errorLower.includes('verification')) {
          setPendingNotice(
            errorMsg ||
              'Your account is under review. You can log in once we verify your business documents, usually within 1-3 business days.',
          );
          toast.info(errorMsg || 'Your account is under review.');
        } else if (errorLower.includes('onboarding')) {
          setPendingNotice(errorMsg || 'Please complete your onboarding before signing in.');
          toast.info(errorMsg || 'Please complete your onboarding first.');
        } else {
          toast.error(errorMsg || 'Your account is inactive or suspended. Please contact support.');
        }
        setLoading(false);
        return;
      }

      if (errorLower.includes('email') && (errorLower.includes('not') || errorLower.includes('not found'))) {
        toast.error('Email not found. Please check your email or create a new account.');
      } else if (errorLower.includes('password') && errorLower.includes('incorrect')) {
        toast.error('Incorrect password. Please try again.');
      } else if (errorLower.includes('inactive') || errorLower.includes('suspended')) {
        toast.error('Your account is inactive or suspended. Please contact support.');
      } else if (err.response?.data?.errors) {
        Object.values(err.response.data.errors).flat().forEach((msg: any) => {
          toast.error(msg);
        });
      } else {
        toast.error(errorMsg || 'Login failed. Please check your credentials.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] bg-emerald-950 flex-col p-12 sticky top-0 h-screen">
        <div className="mb-auto">
          <div className="flex flex-col">
            <img src="/assets/logo/logo.svg" className="h-10 w-auto object-contain brightness-0 invert" alt="ShoplyCircle Logo" />
            <span className="text-[10px] uppercase tracking-widest text-emerald-300 font-extrabold mt-2 ml-0.5 leading-none">Manufacturer Hub</span>
          </div>
        </div>
        <div className="space-y-8">
          <LoginMascot label="ShoplyCircle manufacturer mascot" />
          <div>
            <h1 className="text-4xl font-extrabold text-white leading-tight mb-4">
              Run production<br />with clarity
            </h1>
            <p className="text-emerald-100/60 leading-relaxed text-lg">
              Manage your catalog, orders, and payouts from one focused dashboard.
            </p>
          </div>
          <ul className="space-y-4 text-sm text-white/80">
            <li className="flex items-center gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" />
              Products, bundles, and inventory control
            </li>
            <li className="flex items-center gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" />
              Order fulfillment and reseller network
            </li>
            <li className="flex items-center gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" />
              Earnings, analytics, and payouts
            </li>
          </ul>
        </div>
        <div className="mt-auto pt-8 text-xs text-emerald-100/40">
          Shoplycircle Manufacturer Hub
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex flex-col items-center gap-1.5 mb-10 justify-center">
            <img src="/assets/logo/logo.svg" className="h-9 w-auto object-contain" alt="ShoplyCircle Logo" />
            <span className="text-[10px] uppercase tracking-widest text-emerald-600 font-extrabold leading-none">Manufacturer Hub</span>
            <LoginMascot label="ShoplyCircle manufacturer mascot" />
          </div>

          <Card className="w-full border border-gray-200 shadow-sm">
            <CardHeader className="text-center pb-4 px-6 pt-8">
              <CardTitle className="text-2xl font-bold text-gray-900">
                Sign in to your account
              </CardTitle>
              <p className="text-gray-500">
                Access your manufacturer dashboard
              </p>
            </CardHeader>

            <CardContent className="px-6 pb-8">
              {pendingNotice && (
                <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
                  <Clock className="h-5 w-5 shrink-0 mt-0.5" />
                  <div className="text-sm leading-relaxed">
                    <p className="font-bold">Account under review</p>
                    <p className="mt-0.5">{pendingNotice}</p>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <div className="relative group">
                    <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors ${
                      fieldErrors.email ? 'text-red-400' : 'text-gray-400 group-focus-within:text-emerald-600'
                    }`} />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="contact@company.com"
                      value={formData.email}
                      aria-invalid={!!fieldErrors.email}
                      aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                      onChange={e => {
                        setFormData({ ...formData, email: e.target.value });
                        clearFieldError('email');
                      }}
                      className={`pl-12 h-12 rounded-lg ${
                        fieldErrors.email
                          ? 'border-red-400 focus:ring-4 focus:ring-red-500/10 focus:border-red-500'
                          : 'border-gray-200 focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-600'
                      }`}
                    />
                  </div>
                  {fieldErrors.email && (
                    <p id="email-error" className="ml-1 text-xs font-semibold text-red-500">{fieldErrors.email}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="relative group">
                    <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors ${
                      fieldErrors.password ? 'text-red-400' : 'text-gray-400 group-focus-within:text-emerald-600'
                    }`} />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      value={formData.password}
                      aria-invalid={!!fieldErrors.password}
                      aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                      onChange={e => {
                        setFormData({ ...formData, password: e.target.value });
                        clearFieldError('password');
                      }}
                      className={`pl-12 pr-12 h-12 rounded-lg ${
                        fieldErrors.password
                          ? 'border-red-400 focus:ring-4 focus:ring-red-500/10 focus:border-red-500'
                          : 'border-gray-200 focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-600'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-emerald-600 transition-colors p-1"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {fieldErrors.password && (
                    <p id="password-error" className="ml-1 text-xs font-semibold text-red-500">{fieldErrors.password}</p>
                  )}
                </div>

                <div className="flex items-center justify-end -mt-2">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-center text-sm text-gray-500 mt-6">
                Ready to start manufacturing?{' '}
                <Link to="/register" className="text-emerald-600 font-semibold hover:text-emerald-700 hover:underline transition-colors">
                  Register as a manufacturer
                </Link>
              </p>
            </CardContent>
          </Card>

          <div className="flex items-center justify-center gap-2 mt-6 text-xs text-gray-400">
            <Shield className="h-4 w-4 text-emerald-500" />
            <span>Secure enterprise authentication</span>
          </div>
        </div>
      </div>
    </div>
  );
}
